/**
 * Application Constants
 * Centralized constants for the entire application
 */

// API Configuration
export const API_CONFIG = {
  BASE_URL: process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/api',
  TIMEOUT: 10000,
  RETRY_ATTEMPTS: 3,
};

// Pagination
export const PAGINATION = {
  DEFAULT_PAGE_SIZE: 10,
  PAGE_SIZE_OPTIONS: [10, 25, 50, 100],
  DEFAULT_PAGE: 1,
};

// Local Storage Keys
export const STORAGE_KEYS = {
  AUTH_TOKEN: 'authToken',
  REFRESH_TOKEN: 'refreshToken',
  USER_DATA: 'userData',
  THEME: 'theme',
  LANGUAGE: 'language',
};

// Routes
export const ROUTES = {
  HOME: '/',
  DASHBOARD: '/dashboard',
  LOGIN: '/login',
  LEADS: {
    BASE: '/leads',
    NEW: '/leads/new',
    IN_PROCESS: '/leads/in-process',
    HOLD: '/leads/hold',
  },
  APPLICATIONS: {
    BASE: '/applications',
    NEW: '/applications/new',
    IN_PROCESS: '/applications/in-process',
    HOLD: '/applications/hold',
    RECOMMENDED: '/applications/recommended',
    SENT_BACK: '/applications/sent-back',
  },
  SANCTIONED: '/sanctioned',
  DISBURSAL: {
    BASE: '/disbursal',
    NEW: '/disbursal/new',
    IN_PROCESS: '/disbursal/in-process',
    SEND_BACK: '/disbursal/send-back',
    HOLD: '/disbursal/hold',
    PENDING: '/disbursal/pending',
    DISBURSED: '/disbursal/disbursed',
    WAIVED_OFF: '/disbursal/waived-off',
  },
  REJECTED_APPS: '/rejected-apps',
  VERIFICATION_STATUS: '/verification-status',
  CUSTOMER_FEEDBACK: '/customer-feedback',
  EXPORT: '/export',
  MIS_REPORT: '/mis-report',
  BLACKLIST_USER: '/blacklist-user',
};

// Status Options
export const STATUS_OPTIONS = {
  LEAD: ['NEW', 'IN_PROCESS', 'HOLD', 'COMPLETED'],
  APPLICATION: ['NEW', 'IN_PROCESS', 'HOLD', 'RECOMMENDED', 'SENT_BACK', 'APPROVED', 'REJECTED'],
  DISBURSAL: ['NEW', 'IN_PROCESS', 'PENDING', 'DISBURSED', 'HOLD', 'SENT_BACK', 'WAIVED_OFF'],
  VERIFICATION: ['PENDING', 'VERIFIED', 'REJECTED'],
};

// User Types
export const USER_TYPES = {
  NEW: 'NEW',
  REPEAT: 'REPEAT',
  EXISTING: 'EXISTING',
};

// Lead Sources
export const LEAD_SOURCES = [
  'DHANWALLE',
  'WEBSITE',
  'REFERRAL',
  'SOCIAL_MEDIA',
  'OTHER',
];

// States (Indian States)
export const INDIAN_STATES = [
  'ANDHRA_PRADESH',
  'ARUNACHAL_PRADESH',
  'ASSAM',
  'BIHAR',
  'CHHATTISGARH',
  'GOA',
  'GUJARAT',
  'HARYANA',
  'HIMACHAL_PRADESH',
  'JHARKHAND',
  'KARNATAKA',
  'KERALA',
  'MADHYA_PRADESH',
  'MAHARASHTRA',
  'MANIPUR',
  'MEGHALAYA',
  'MIZORAM',
  'NAGALAND',
  'ODISHA',
  'PUNJAB',
  'RAJASTHAN',
  'SIKKIM',
  'TAMIL_NADU',
  'TELANGANA',
  'TRIPURA',
  'UTTAR_PRADESH',
  'UTTARAKHAND',
  'WEST_BENGAL',
  'DELHI',
];

// Date Formats
export const DATE_FORMATS = {
  DISPLAY: 'DD-MM-YYYY',
  API: 'YYYY-MM-DD',
  DATETIME: 'DD-MM-YYYY HH:mm',
  DATETIME_API: 'YYYY-MM-DD HH:mm:ss',
};

// Error Messages
export const ERROR_MESSAGES = {
  NETWORK_ERROR: 'Network error. Please check your internet connection.',
  UNAUTHORIZED: 'You are not authorized. Please login again.',
  FORBIDDEN: 'You do not have permission to access this resource.',
  NOT_FOUND: 'The requested resource was not found.',
  SERVER_ERROR: 'Server error. Please try again later.',
  VALIDATION_ERROR: 'Please check your input and try again.',
  UNKNOWN_ERROR: 'An unexpected error occurred. Please try again.',
};

// Success Messages
export const SUCCESS_MESSAGES = {
  CREATED: 'Record created successfully.',
  UPDATED: 'Record updated successfully.',
  DELETED: 'Record deleted successfully.',
  EXPORTED: 'Data exported successfully.',
  REPORT_GENERATED: 'Report generated successfully.',
};

// Table Configuration
export const TABLE_CONFIG = {
  DEFAULT_SORT_COLUMN: 'id',
  DEFAULT_SORT_ORDER: 'desc',
  MIN_WIDTH: 1200,
};

// Export Options
export const EXPORT_OPTIONS = [
  'SELECT',
  'A/C REPORTS',
  'BLACK LISTED',
  'BOB BANK CMS SHEET',
  'BOB DISBURSAL',
  'BOB RECOVERY',
  'CALL LOAN FOLLOWUP',
  'CHECK COLLECTION REPORT',
  'CIBIL REPORT',
  'COLLECTION',
  'LEAD DUPLICATE',
  'LEAD REJECTED',
  'LEAD TOTAL',
  'LEGAL NOTICE DATA',
  'LOAN CLOSE',
  'LOAN DISBURSE HOLD',
  'LOAN DISBURSE SEND BACK',
  'LOAN DISBURSED',
  'LOAN PENDING',
  'LOAN RECOVERY',
  'LOAN REPAYMENT',
  'LOAN SANCTIONED',
  'MIS REPORT',
  'PAYMENT RECEIVED',
  'RECOVERY REPORT',
  'REJECTED APPLICATIONS',
  'SANCTIONED LOANS',
];

// MIS Report Options
export const MIS_REPORT_OPTIONS = [
  'SELECT',
  'COLLECTION (AFTER 10 DPD) - RECOVERY',
  'COLLECTION (ON BEFORE DUE DATE) - PRE-COLLECTION',
  'COLLECTION (UPTO 10 DPD) - COLLECTION',
  'COLLECTION : BRANCH WISE BY PAYMENT DATE',
  'COLLECTION : BRANCH WISE BY REPAY DATE',
  'COLLECTION : COLLECTION EXECUTIVE BY PAYMENT DATE',
  'COLLECTION : COLLECTION EXECUTIVE BY REPAY DATE',
  'COLLECTION : SANCTION EXECUTIVE BY PAYMENT DATE',
  'COLLECTION : SANCTION EXECUTIVE BY REPAY DATE',
  'COLLECTION BUCKET-WISE REPORT (AMOUNT)',
  'COLLECTION BUCKET-WISE REPORT (CASES)',
  'COLLECTION CALL FOLLOWUP STATUS WISE',
  'COLLECTION CALL FOLLOWUP WITH TIME',
  'HOURLY DISBURSAL REPORT',
  'HOURLY STATUS WISE REPORT',
  'LEAD CONVERSION REPORT (FRESH)',
  'LEAD CONVERSION REPORT (REPEAT)',
];

